import { MinsterNumberPickerComponent } from "../ui-components/minster-number-picker/minster-number-picker.component";
import { MinsterShowHideComponent } from "../ui-components/minster-show-hide/minster-show-hide.component";
import { MinsterCheckboxComponent } from "../ui-components/minster-checkbox/minster-checkbox.component";
import { MinsterSelectComponent } from "../ui-components/minster-select/minster-select.component";
import { ControlContainer, FormsModule, NgForm, NgModelGroup, Validators } from '@angular/forms';
import { MinsterInputComponent } from '../ui-components/minster-input/minster-input.component';
import { MinsterCardComponent } from "../ui-components/minster-card/minster-card.component";
import { MinsterTelComponent } from '../ui-components/minster-tel/minster-tel.component';
import { Accident, Claim, Occupant } from '../../models/claim.model';
import { ScreenBaseComponent } from '../screen-base/screen-base.component';
import { ScreenType, InputType } from '../../models/enums.models';
import { ClaimsService } from '../../services/claims.service';
import { StateService } from '../../services/state.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Component, ViewChild } from '@angular/core';
import { LookUp } from '../../models/look-up.model';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Component({
    selector: 'app-your-accident-your-involvement',
    standalone: true,
    imports: [MinsterCardComponent, MinsterSelectComponent, MinsterCheckboxComponent, FormsModule, MatButtonModule,
        MinsterNumberPickerComponent, MinsterInputComponent, MinsterShowHideComponent, MatIconModule, MinsterTelComponent],
    templateUrl: './your-accident-your-involvement.component.html',
    styleUrl: './your-accident-your-involvement.component.css',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})

export class YourAccidentYourInvolvementComponent extends ScreenBaseComponent {
    @ViewChild(NgModelGroup) _formGroup: NgModelGroup | undefined;

    _accident: Accident;
    _claim: Claim;
    _driver: Occupant;
    _inputTypes = InputType;
    _seatBelts: LookUp[];
    _productType: LookUp[];
    _claimantType: LookUp[];
    _claimantTypeFilter: LookUp[] = [];
    _telTypes: LookUp[];
    _readonly: boolean = false;
    _numberOfDrivers: number = 0;

    constructor(stateService: StateService, router: Router, toastr: ToastrService, claimService: ClaimsService) {
        super(router, ScreenType.YourAccidentYourInvolvement, toastr, stateService, claimService);

        this._readonly = stateService.formIsSubmitted;
        this._accident = stateService.claim?.accident!;
        this._claim = stateService.claim!;

        this.initializeOccupantDriver();
        this._driver = this.getDriver();

        this._telTypes = stateService.lookUps?.lookUpDictionary['TelephoneNumberType']!;
        this._seatBelts = stateService.lookUps!.lookUpDictionary['SeatBeltIsWorn'];
        this._productType = stateService.lookUps!.lookUpDictionary['ProductType'];
        this._claimantType = stateService.lookUps!.lookUpDictionary['ClaimantType'];

        this.onTypeOfVehicleChange(this._claim.productTypeId);
    }

    private initializeOccupantDriver(): void {
        if (!this._claim.occupants) {
            this._claim.occupants = [];
        }

        if (this._claim.occupants.length === 0) {
            this._claim.occupants.push({
                occupantId: 0,
                titleId: 0,
                forename: '',
                surname: '',
                isInjured: null,
                isDriver: true,
                emailAddress: '',
                addressId: 0,
                updatedBy: '',
                passengerInjuryStatusId: 0,
                claimId: this._claim.claimDetailId,
                address: undefined,
                occupantContactDetails: [],
                accordionOpen: false
            });
        }

        const driver = this._claim.occupants.find(c => c.isDriver)!;
        if (!driver.occupantContactDetails) {
            driver.occupantContactDetails = [];
        }

        if (driver.occupantContactDetails.length === 0) {
            driver.occupantContactDetails.push({
                contactDetailId: 0,
                occupantId: driver.occupantId,
                telephoneNumberTypeId: 1,
                telephoneNumber: '',
                isPrimary: true,
                updatedBy: ''
            });
        }
    }

    get primaryTelephoneNumber(): string {
        return this.getDriver()?.occupantContactDetails?.[0]?.telephoneNumber || '';
    }

    set primaryTelephoneNumber(value: string) {
        const driver = this.getDriver();
        if (driver?.occupantContactDetails?.[0]) {
            driver.occupantContactDetails[0].telephoneNumber = value;
            this.updatePrimaryStatus();
        }
    }

    get primaryPhoneType(): 'mobile' | 'landline' {
        const contact = this.getDriver()?.occupantContactDetails?.[0];
        return contact?.telephoneNumberTypeId === 1 ? 'mobile' : 'landline';
    }

    set primaryPhoneType(value: 'mobile' | 'landline') {
        const driver = this.getDriver();
        if (driver?.occupantContactDetails?.[0]) {
            driver.occupantContactDetails[0].telephoneNumberTypeId = value === 'mobile' ? 1 : 2;
            this.updatePrimaryStatus();
        }
    }

    get primaryTelephoneTypeId(): number {
        return this.getDriver()?.occupantContactDetails?.[0]?.telephoneNumberTypeId || 1;
    }

    set primaryTelephoneTypeId(value: number) {
        const driver = this.getDriver();
        if (driver?.occupantContactDetails?.[0]) {
            driver.occupantContactDetails[0].telephoneNumberTypeId = value;
            this.updatePrimaryStatus();
        }
    }

    get secondaryTelephoneNumber(): string {
        return this.getDriver()?.occupantContactDetails?.[1]?.telephoneNumber || '';
    }

    set secondaryTelephoneNumber(value: string) {
        const driver = this.getDriver();
        if (driver?.occupantContactDetails?.[1]) {
            driver.occupantContactDetails[1].telephoneNumber = value;
            this.updatePrimaryStatus();
        }
    }

    get secondaryPhoneType(): 'mobile' | 'landline' {
        const contact = this.getDriver()?.occupantContactDetails?.[1];
        return contact?.telephoneNumberTypeId === 1 ? 'mobile' : 'landline';
    }

    set secondaryPhoneType(value: 'mobile' | 'landline') {
        const driver = this.getDriver();
        if (driver?.occupantContactDetails?.[1]) {
            driver.occupantContactDetails[1].telephoneNumberTypeId = value === 'mobile' ? 1 : 2;
            this.updatePrimaryStatus();
        }
    }

    get secondaryTelephoneTypeId(): number {
        return this.getDriver()?.occupantContactDetails?.[1]?.telephoneNumberTypeId || 1;
    }

    set secondaryTelephoneTypeId(value: number) {
        const driver = this.getDriver();
        if (driver?.occupantContactDetails?.[1]) {
            driver.occupantContactDetails[1].telephoneNumberTypeId = value;
            this.updatePrimaryStatus();
        }
    }

    get hasSecondaryPhone(): boolean {
        const driver = this.getDriver();
        return (driver?.occupantContactDetails?.length || 0) > 1;
    }

    get canAddPhone(): boolean {
        const contactDetails = this.getDriver()?.occupantContactDetails ?? [];
        return contactDetails.length < 2 && this.isPhoneNumberValid(contactDetails[0]?.telephoneNumber, contactDetails[0]?.telephoneNumberTypeId);
    }

    private updatePrimaryStatus(): void {
        const driver = this.getDriver();
        if (!driver || !driver.occupantContactDetails) {
            return;
        }

        const primary = driver.occupantContactDetails[0];
        const secondary = driver.occupantContactDetails[1];

        if (primary) {
            primary.isPrimary = true;
        }
        if (secondary) {
            if (primary?.telephoneNumberTypeId === secondary.telephoneNumberTypeId) {
                secondary.isPrimary = false;
            } else {
                secondary.isPrimary = true;
            }
        }
    }

    private getDriver(): Occupant {
        return this._claim.occupants.find(c => c.isDriver)!;
    }

    override isScreenValid(): boolean {
        return this.stateService.getFormValidationErrorsExcludeRequired(this._formGroup!.control) == 0;
    }

    override onNext(): void {
        if(this._readonly){
            super.onNext();
            return;
        }
        let hasErrors = false;
        this.updateValidators();

        if (this._formGroup && this._formGroup.invalid) {
            Object.keys(this._formGroup.control.controls).forEach((key) => {
                const control = this._formGroup!.control.get(key);

                if (control) {
                    const errors = control.errors;

                    if (errors) {
                        const hasOnlyRequiredError = Object.keys(errors).length === 1 && errors['required'];
                        if (this.stateService.formIsSubmitted || !hasOnlyRequiredError) {
                            hasErrors = true;
                        }
                    }

                    control.markAsTouched();
                    control.markAsDirty();
                    control.updateValueAndValidity();
                }
            });

            const phoneControls = [
                this._formGroup.control.get(this._fcn.AccYourInvolvement.DriverPrimaryPhoneNo),
                this._formGroup.control.get(this._fcn.AccYourInvolvement.DriverSecondaryPhoneNo)
            ];

            phoneControls.forEach(control => {
                if (control) {
                    const errors = control.errors;

                    if (errors) {
                        const hasOnlyRequiredError = Object.keys(errors).length === 1 && errors['required'];

                        if (this.stateService.formIsSubmitted || !hasOnlyRequiredError) {
                            hasErrors = true;
                        }
                    }

                    control.markAsTouched();
                    control.markAsDirty();
                    control.updateValueAndValidity();
                }
            });

            if (hasErrors) {
                this.toastr.warning('Please fix the form errors before proceeding.');
                this.stateService.enableSaving = false;
                return;
            }
        }

        if (this.stateService.formIsSubmitted && this._formGroup!.invalid) {
            this.toastr.warning('Please fix the required fields before proceeding.');
            this.stateService.enableSaving = false;
            return;
        }

        if (this._numberOfDrivers < 0 || this._numberOfDrivers > 1) {
            this.toastr.warning('Number of drivers can only be 0 or 1');
            this.stateService.enableSaving = false;
            return;
        }

        if (!this.isDriver && this._claim.numberOfPassengers < 1) {
            this.toastr.warning('Number of passengers must be at least 1');
            this.stateService.enableSaving = false;
            return;
        }


        const driver = this.getDriver();
        driver.occupantContactDetails = driver.occupantContactDetails.filter(contact =>
            contact.telephoneNumber?.trim() !== '' || contact.isPrimary
        );

        this._claim.numberOfOccupants = this._claim.numberOfPassengers + this._numberOfDrivers;
        this.stateService.enableSaving = this.hasStateChanged();

        this.stateService.formInvolvedAccidentSaved = true; 

        super.onNext();
    }

    get isBusCarVanLorryPassenger(): boolean {
        const isBusCarVanLorryPassenger = this._claim.claimant.claimantTypeId === 9 || this._claim.claimant.claimantTypeId === 8;

        if (!isBusCarVanLorryPassenger) {
            this._claim.accident.hasEvidenceOfTravel = null;
            const control = this._formGroup?.control!.get(this._fcn.AccYourInvolvement.EvidenceOfTravel);

            if (control) {
                control.clearValidators();
                control.updateValueAndValidity();
            }
        }
        else {
            const control = this._formGroup?.control!.get(this._fcn.AccYourInvolvement.EvidenceOfTravel);

            if (control) {
                control.setValidators([Validators.required]);
                control.updateValueAndValidity();
            }
        }

        return isBusCarVanLorryPassenger;
    }

    get isDriver(): boolean {
        var isDriver = this._claim.claimant.claimantTypeId === 2;

        if (!isDriver) {
            this._claim.isVehicleOwner = false;
        }

        return isDriver;
    }

    get isRequired(): boolean {
        return this.stateService.formIsSubmitted && !this.isDriver;
    }

    get isPassenger(): boolean {
        const isPassenger = this._claim.claimant.claimantTypeId === 8 || this._claim.claimant.claimantTypeId === 9;

        const controls = [
            this._formGroup?.control!.get(this._fcn.AccYourInvolvement.DriverAtFault),
            this._formGroup?.control!.get(this._fcn.AccYourInvolvement.DriverFirstName),
            this._formGroup?.control!.get(this._fcn.AccYourInvolvement.DriverSurname),
            this._formGroup?.control!.get(this._fcn.AccYourInvolvement.DriverEmail),
        ];

        if (!isPassenger) {
            this._claim.claimVehicle.isDriverAtFault = false;

            const driver = this.getDriver();
            driver.surname = '';
            driver.forename = '';
            driver.emailAddress = '';
            driver.occupantContactDetails = [];

            controls.forEach(control => {
                control?.clearValidators();
                control?.updateValueAndValidity();
            });
        }
        else {
            controls[0]?.setValidators([Validators.required]);
            controls[1]?.setValidators([Validators.required, Validators.minLength(2), Validators.maxLength(50)]);
            controls[2]?.setValidators([Validators.required, Validators.minLength(2), Validators.maxLength(50)]);
            controls[3]?.setValidators([Validators.required, Validators.email]);

            controls.forEach(control => control?.updateValueAndValidity());
        }

        return isPassenger;
    }

    get numberOfDrivers(): number {
        return this._numberOfDrivers;
    }

    set numberOfDrivers(value: number) {
        this._numberOfDrivers = value;
    }

    onHowInvolvedChange(involvedId: number | null): void {
        if (involvedId === 9 || involvedId === 8) {
            const driver = this.getDriver();

            if (driver.occupantContactDetails!.length >= 1) {
                return;
            }
            else {
                driver.occupantContactDetails!.push({
                    contactDetailId: 0,
                    occupantId: driver.occupantId,
                    telephoneNumberTypeId: 1,
                    telephoneNumber: '',
                    isPrimary: true,
                    updatedBy: ''
                });
            }
            this._numberOfDrivers = 0;
        }
        else if (involvedId === 2) {
            this._numberOfDrivers = 1;
        }

        this.updateValidators();
    }

    onTypeOfVehicleChange(typeOfVehicleId: number | null): void {
        switch (typeOfVehicleId) {
            case 1:
                this._claimantTypeFilter = this._claimantType.filter(x => x.value === 2 || x.value === 8);
                break;
            case 2:
                this._claimantTypeFilter = this._claimantType.filter(x => x.value === 11 || x.value === 12);
                break;
            case 3:
                this._claimantTypeFilter = this._claimantType.filter(x => x.value === 2 ||x.value === 9);
                break;
            default:
                this._claimantTypeFilter = this._claimantType.filter(x => x.value === 2 || x.value === 8 || x.value === 9);
                break;
        }
        
    }


    onAddPhoneNumber(): void {
        const driver = this.getDriver();

        if (!driver.occupantContactDetails || driver.occupantContactDetails.length === 0) {
            driver.occupantContactDetails = [
                {
                    contactDetailId: 0,
                    occupantId: driver.occupantId,
                    telephoneNumberTypeId: 1,
                    telephoneNumber: '',
                    isPrimary: true,
                    updatedBy: ''
                },
                {
                    contactDetailId: 0,
                    occupantId: driver.occupantId,
                    telephoneNumberTypeId: 2,
                    telephoneNumber: '',
                    isPrimary: true,
                    updatedBy: ''
                }
            ];

            return;
        }

        if (driver.occupantContactDetails.length >= 2) {
            this.toastr.warning('You can only add one additional phone number.');
            return;
        }

        //If there's already one number in, just add second phone number.
        driver.occupantContactDetails.push({
            contactDetailId: 0,
            occupantId: driver.occupantId,
            telephoneNumberTypeId: 2,
            telephoneNumber: '',
            isPrimary: true,
            updatedBy: ''
        });
    }

    onRemoveAdditionalPhoneNumber(): void {
        const driver = this.getDriver();

        if (driver.occupantContactDetails!.length > 1) {
            driver.occupantContactDetails!.splice(1, 1);
        }
        else {
            this.toastr.warning('You cannot remove the primary phone number.');
        }
    }

    onPhoneTypeChange(): void {
        const driver = this.getDriver();
        const contactDetails = driver.occupantContactDetails!;

        if (contactDetails.length === 1) {
            contactDetails[0].isPrimary = true;
            return;
        }

        contactDetails[0].isPrimary = true;
        contactDetails[1].isPrimary = contactDetails[0].typeId !== contactDetails[1].typeId && !!contactDetails[1].typeId;
    }

    private isPhoneNumberValid(phoneNumber: string, type: number): boolean {
        const phoneRegex = type === 1 ? /^07[0-9]{8,15}$/ : /^(?:01|02|03|08)[0-9]{8,15}$/;
        return phoneRegex.test(phoneNumber) && phoneNumber?.trim().length > 0;
    }

    updateValidators(): void {
        const controls = [
            { formName: this._fcn.AccYourInvolvement.EvidenceOfTravel, makeRequired: this.isBusCarVanLorryPassenger },
            { formName: this._fcn.AccYourInvolvement.AreYouVehicleOwner, makeRequired: this.isDriver },
            { formName: this._fcn.AccYourInvolvement.DriverPrimaryPhoneNo, makeRequired: this.isRequired },
            { formName: this._fcn.AccYourInvolvement.DriverSecondaryPhoneNo, makeRequired: this.isRequired && this.hasSecondaryPhone }
        ];

        controls.forEach(({ formName, makeRequired }) => {
            const control = this._formGroup?.control.get(formName);

            if (control) {
                if (makeRequired && !control.hasValidator(Validators.required)) {
                    control.setValidators([Validators.required]);
                }
                else if (!makeRequired && control.validator) {
                    control.clearValidators();
                }

                control.updateValueAndValidity();
            }
        });

        const passengerControls = [
            { control: this._formGroup?.control.get(this._fcn.AccYourInvolvement.DriverAtFault), validators: this.isPassenger ? [Validators.required] : [] },
            { control: this._formGroup?.control.get(this._fcn.AccYourInvolvement.DriverFirstName), validators: this.isPassenger ? [Validators.required, Validators.minLength(2), Validators.maxLength(50)] : [] },
            { control: this._formGroup?.control.get(this._fcn.AccYourInvolvement.DriverSurname), validators: this.isPassenger ? [Validators.required, Validators.minLength(2), Validators.maxLength(50)] : [] },
            { control: this._formGroup?.control.get(this._fcn.AccYourInvolvement.DriverEmail), validators: this.isPassenger ? [Validators.required, Validators.email] : [] }
        ];

        passengerControls.forEach(({ control, validators }) => {
            if (control) {
                if (validators.length > 0) {
                    control.setValidators(validators);
                }
                else {
                    control.clearValidators();
                }

                control.updateValueAndValidity();
            }
        });
    }
}
